import React, { MouseEvent, ReactElement, useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';

import classnames from 'classnames/bind';

import COLOR from '@CONSTANTS/COLOR.constant';

import { Paperclip, Copy, Edit2, Trash2, Eye, Clock } from 'lucide-react';

import CircularProgressIndicator from '@COMPONENTS/SHARED/CircularProgressIndicator';
import Lock from '@COMPONENTS/SHARED/Lock';
import Tooltip from '@COMPONENTS/SHARED/Tooltip';

import styles from './RoundIconButton.module.scss';

const cx: CX = classnames.bind(styles);

const icons: { [key: string]: ReactElement } = {
    edit: <Edit2 size={16} strokeWidth={2} />,
    copy: <Copy size={16} strokeWidth={2} />,
    'copy-lucide': <Copy size={16} strokeWidth={2} />,
    delete: <Trash2 size={16} strokeWidth={2} />,
    preview: <Eye size={16} strokeWidth={2} />,
    history: <Clock size={16} strokeWidth={2} />,
    attachment: <Paperclip size={16} strokeWidth={2} />,
};

function RoundIconButton(props: Props) {
    const {
        tooltipTitle,
        locked = false,
        icon,
        onClick,
        isProcessing = false,
        disabled = false,
        showBadge = false,
        badgeCount,
    } = props;

    const [tooltipId] = useState(Tooltip.getId());

    function handleClick(event: MouseEvent<HTMLButtonElement>) {
        if (!locked) {
            event.preventDefault();

            onClick(event);
        }
    }

    const Icon = icons[icon];
    const shouldShowBadge = showBadge && badgeCount !== undefined && badgeCount > 0;

    return (
        <div className={cx('round-button-wrapper')}>
            <button
                id={tooltipId}
                type="button"
                className={cx('clear-button', 'round-button', {
                    locked,
                })}
                style={{
                    pointerEvents: (disabled || locked) ? 'none' : undefined,
                }}
                onClick={handleClick}
            >
                {
                    locked
                    && (
                        <div className={cx('lock-wrapper')}>
                            <Lock size="free" />
                        </div>
                    )
                }

                {
                    !isProcessing
                        ? (
                            <div className={cx('icon', icon)}>{Icon}</div>
                        ) : (
                            <CircularProgressIndicator
                                size={20}
                                color={COLOR['marriott-primary']}
                                thickness={2}
                            />
                        )
                }
            </button>
            
            {shouldShowBadge && (
                <div className={cx('badge-counter')}>
                    {badgeCount > 99 ? '99+' : badgeCount}
                </div>
            )}
            
            <Tooltip
                id={tooltipId}
                content={tooltipTitle}
                disabled={locked}
            />
        </div>
    );
}

RoundIconButton.defaultProps = {
    locked: false,
    isProcessing: false,
    disabled: false,
    showBadge: false,
    badgeCount: undefined,
};

RoundIconButton.propTypes = {
    tooltipTitle: PropTypes.string.isRequired,
    locked: PropTypes.bool,
    icon: PropTypes.oneOf(['copy', 'copy-lucide', 'edit', 'delete', 'preview', 'history', 'attachment']).isRequired,
    onClick: PropTypes.func.isRequired,
    isProcessing: PropTypes.bool,
    disabled: PropTypes.bool,
    showBadge: PropTypes.bool,
    badgeCount: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([undefined])]),
};

interface RoundIconButtonProps {
    tooltipTitle: string;
    locked?: boolean;
    icon: 'copy' | 'copy-lucide' | 'edit' | 'delete' | 'preview' | 'history' | 'attachment';
    onClick: (event: MouseEvent<HTMLButtonElement>) => void;
    isProcessing?: boolean;
    disabled?: boolean;
    showBadge?: boolean;
    badgeCount?: number;
}

type Props = RoundIconButtonProps;

export default RoundIconButton;
